import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Lock, Link2, Zap, CalendarClock, Banknote } from "lucide-react";
import { api } from "@/lib/api";
import { BILL_CATEGORIES, catColor, fmt, weekdayShort } from "@/lib/constants";
import BillModal from "@/components/BillModal";
import HelpTip from "@/components/HelpTip";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function CalendarPage() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ plan: null, occurrences: [], paydays: [] });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/calendar?year=${cursor.getFullYear()}&month=${cursor.getMonth() + 1}`).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startWeekday = first.getDay(); // Sun=0
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor]);

  const billsByDay = useMemo(() => {
    const map = {};
    data.occurrences.forEach(o => {
      const k = parseInt(o.occurrence_date.slice(8, 10), 10);
      (map[k] = map[k] || []).push(o);
    });
    return map;
  }, [data]);

  const paydaysByDay = useMemo(() => {
    const map = {};
    (data.paydays || []).forEach(p => {
      const k = parseInt(p.date.slice(8, 10), 10);
      (map[k] = map[k] || []).push(p);
    });
    return map;
  }, [data]);

  const monthIncome = (data.paydays || []).reduce((s, p) => s + p.amount, 0);

  const monthTotal = data.occurrences.reduce((s, o) => s + o.planned_amount, 0);
  const monthExtra = data.occurrences.reduce((s, o) => s + o.planned_extra, 0);

  const today = new Date();
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold">Wall Calendar</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{monthLabel}</h1>
          <div className="text-xs text-muted-foreground mt-1" data-testid="cal-month-summary">
            <span className="font-mono font-semibold text-foreground">{fmt(monthTotal)}</span> planned across {data.occurrences.length} payments
            {data.plan && monthExtra > 0 && <> · <span className="text-indigo-400 font-semibold">{fmt(monthExtra)}</span> {data.plan.strategy} boost</>}
            {monthIncome > 0 && <> · <span className="text-emerald-500 font-semibold" data-testid="cal-month-income">{fmt(monthIncome)}</span> income</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            data-testid="cal-prev" className="p-2 rounded-lg border border-border hover:bg-accent"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor(startOfMonth(new Date()))} className="px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-accent">Today</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            data-testid="cal-next" className="p-2 rounded-lg border border-border hover:bg-accent"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="cal-add-bill"
            className="ml-2 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition">
            <Plus className="h-4 w-4" /> Add Bill
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        <div className="grid grid-cols-7 bg-accent/50 border-b border-border">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="p-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-fr">
          {grid.map((d, i) => {
            const isToday = d && isSameDay(d, today);
            const dayBills = d ? (billsByDay[d.getDate()] || []) : [];
            const dayPaydays = d ? (paydaysByDay[d.getDate()] || []) : [];
            const isPayday = dayPaydays.length > 0;
            return (
              <div key={d ? d.toISOString() : `empty-${i}`} data-testid={d ? `cal-day-${d.getDate()}` : undefined}
                className={`min-h-[110px] sm:min-h-[130px] p-1.5 border-r border-b border-border/60 relative ${
                  !d ? "bg-accent/30" : "bg-card hover:bg-accent/30 transition-colors"
                } ${isToday ? "ring-2 ring-teal-500/50 ring-inset bg-teal-500/5" : isPayday ? "bg-emerald-500/5" : ""}`}>
                {d && (
                  <>
                    <div className={`text-[11px] font-bold ${isToday ? "text-teal-500" : "text-muted-foreground"} mb-1`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {dayPaydays.map((p, pi) => (
                        <div key={`pay-${pi}`} data-testid={`cal-payday-${d.getDate()}-${pi}`}
                          className="bill-tag flex items-center gap-1 font-semibold"
                          style={{ background: "#10b98122", color: "#10b981", border: "1px solid #10b98155" }}
                          title={`Payday · ${p.name} — ${fmt(p.amount)}`}>
                          <Banknote className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{p.name} · {fmt(p.amount)}</span>
                        </div>
                      ))}
                      {dayBills.slice(0, 4).map(b => (
                        <div key={`${b.id}-${b.occurrence_date}`} onClick={() => { setEditing(b); setOpen(true); }}
                          data-testid={`cal-tag-${b.id}-${b.occurrence_date}`}
                          className={`bill-tag flex items-center gap-1 ${b.paid ? "line-through opacity-70" : ""} ${b.is_projection ? "opacity-75" : ""}`}
                          style={{
                            background: catColor(BILL_CATEGORIES, b.category) + (b.is_projection ? "12" : "22"),
                            color: catColor(BILL_CATEGORIES, b.category),
                            border: `1px ${b.is_projection ? "dashed" : "solid"} ${catColor(BILL_CATEGORIES, b.category)}55`,
                          }}
                          title={`${b.name} — ${fmt(b.planned_amount)}${b.planned_extra > 0 ? ` (min ${fmt(b.amount)} + ${fmt(b.planned_extra)} plan boost)` : ""}${b.debt_id ? " · Debt payment" : ""}${b.autopay ? " · Autopay" : ""}${b.is_projection ? " · Projected" : ""}${b.weekend_shifted ? ` · Moved from ${weekdayShort(b.nominal_date)} ${b.nominal_date.slice(8)} (${b.shift_reason || "weekend"})` : ""}`}>
                          {b.weekend_shifted && <CalendarClock className="h-2.5 w-2.5 shrink-0 text-amber-500" data-testid={`cal-shift-${b.id}-${b.nominal_date}`} />}
                          {b.debt_id && <Link2 className="h-2.5 w-2.5 shrink-0" data-testid={`cal-debt-${b.id}`} />}
                          {b.autopay && <Lock className="h-2.5 w-2.5 shrink-0" data-testid={`cal-autopay-${b.id}`} />}
                          <span className="truncate">{b.name} · {fmt(b.planned_amount)}</span>
                          {b.planned_extra > 0 && <Zap className="h-2.5 w-2.5 shrink-0 ml-auto" data-testid={`cal-boost-${b.id}`} />}
                        </div>
                      ))}
                      {dayBills.length > 4 && <div className="text-[10px] text-muted-foreground">+{dayBills.length - 4} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <BillModal open={open} onClose={() => setOpen(false)} bill={editing} onSaved={load} />

      {/* Legend */}
      <div className="flex flex-wrap gap-2 items-center">
        {BILL_CATEGORIES.map(c => (
          <span key={c.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
            style={{ background: c.color + "18", color: c.color, borderColor: c.color + "40" }}>
            <span className="h-2 w-2 rounded-full" style={{ background: c.color }} /> {c.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-emerald-500/40 text-emerald-500 bg-emerald-500/10" data-testid="cal-legend-payday"><Banknote className="h-3 w-3" /> Payday (income)</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-dashed border-muted-foreground/50 text-muted-foreground" data-testid="cal-legend-projected">Dashed = projected occurrence</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-indigo-500/40 text-indigo-400 bg-indigo-500/10" data-testid="cal-legend-boost"><Zap className="h-3 w-3" /> Plan boost on target debt</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/40 text-amber-500 bg-amber-500/10" data-testid="cal-legend-weekend"><CalendarClock className="h-3 w-3" /> Weekend / bank holiday → prior business day <HelpTip k="business_day" /></span>
      </div>
    </div>
  );
}
